/* SinceLastVisitStrip.jsx — iter-181 · operational continuity ribbon.
 *
 * Calm horizontal strip beneath the StageProgression that surfaces
 * "what changed while I was away" — never noisy notifications, never
 * a verbose feed. Acts as the operator's intelligent operational
 * memory so they can orient instantly on return.
 *
 * Visible only when there is at least one meaningful change since
 * the last visit. Falls back to a calm placeholder line when the
 * intel payload hasn't loaded yet.
 *
 * Data: synthesized from `deal.activity`, `deal.documents`, and the
 * intel payload already loaded by the Deal Room. NEVER makes its
 * own API calls — pure derivation, instant render, zero blockers.
 */
import React, { useMemo } from "react";
import { CheckCircle2, FileText, TrendingUp, Sparkles, Clock, ArrowUpRight } from "lucide-react";

const _MS_HOUR = 3_600_000;

const fmtRelative = (iso) => {
  if (!iso) return "just now";
  const t = typeof iso === "number" ? iso : Date.parse(iso);
  if (Number.isNaN(t)) return "just now";
  const diffM = Math.max(0, Math.round((Date.now() - t) / 60_000));
  if (diffM < 1)   return "just now";
  if (diffM < 60)  return `${diffM}m ago`;
  if (diffM < 1440) return `${Math.round(diffM / 60)}h ago`;
  return `${Math.round(diffM / 1440)}d ago`;
};

const STAT_TONE = {
  emerald: { fg: "#047857", bg: "rgba(16,185,129,0.10)" },
  violet:  { fg: "#6D28D9", bg: "rgba(139,92,246,0.10)" },
  amber:   { fg: "#B45309", bg: "rgba(245,158,11,0.12)" },
  indigo:  { fg: "#4338CA", bg: "rgba(99,102,241,0.10)" },
};

/**
 * Derive a compact bundle of "since last visit" highlights from
 * what's already on the deal. Anything missing is silently skipped
 * so the strip stays calm.
 */
function deriveHighlights({ deal, documents, intel, sinceMs }) {
  const cutoff = sinceMs || Date.now() - 24 * _MS_HOUR;

  const recentActs = (deal?.activity || []).filter((a) => {
    const t = typeof a.at === "number" ? a.at : Date.parse(a.at || 0);
    return t >= cutoff;
  });
  const recentDocs = (documents || []).filter((d) => {
    const t = Date.parse(d.uploaded_at || d.created_at || 0);
    return t >= cutoff;
  });

  const out = [];

  // 1) Most meaningful operational event (milestone-style)
  const milestoneAct = recentActs.find((a) =>
    /received|issued|returned|signed|exchanged|completed|offer|memorandum|cleared/i.test(a.text || a.kind || "")
  );
  if (milestoneAct) {
    out.push({
      Icon: CheckCircle2,
      tone: "emerald",
      label: (milestoneAct.text || "").replace(/^[•\-\s]+/, "").slice(0, 48) || "Milestone reached",
    });
  }

  // 2) Updates count (excluding milestone already shown)
  const updatesCount = Math.max(0, recentActs.length - (milestoneAct ? 1 : 0));
  if (updatesCount > 0) {
    out.push({
      Icon: Sparkles,
      tone: "violet",
      label: `${updatesCount} new ${updatesCount === 1 ? "update" : "updates"}`,
    });
  }

  // 3) Documents received
  if (recentDocs.length > 0) {
    out.push({
      Icon: FileText,
      tone: "indigo",
      label: `${recentDocs.length} document${recentDocs.length === 1 ? "" : "s"} received`,
    });
  }

  // 4) Blocker resolved — derived from intel.blockers_resolved if present
  const blockersResolved = Number(intel?.blockers_resolved ?? intel?.since_last_visit?.blockers_resolved ?? 0);
  if (blockersResolved > 0) {
    out.push({
      Icon: CheckCircle2,
      tone: "emerald",
      label: `${blockersResolved} blocker${blockersResolved === 1 ? "" : "s"} resolved`,
    });
  }

  // 5) Exchange probability movement
  const probNow  = Number(intel?.exchange_probability ?? intel?.confidence ?? 0);
  const probPrev = Number(intel?.exchange_probability_prev ?? intel?.confidence_prev ?? 0);
  if (probNow > 0 && (probPrev === 0 || probNow !== probPrev)) {
    const delta = probPrev > 0 ? probNow - probPrev : 0;
    const arrow = delta >= 0 ? " ↗" : " ↘";
    out.push({
      Icon: TrendingUp,
      tone: delta >= 0 ? "emerald" : "amber",
      label: `Exchange probability ${probNow}%${probPrev ? arrow : ""}`,
    });
  }

  return out;
}

export const SinceLastVisitStrip = ({
  deal,
  documents,
  intel,
  lastVisitIso,
  onJumpToTimeline,
  testid = "deal-room-since-last-visit",
}) => {
  const sinceMs = useMemo(() => {
    if (lastVisitIso) {
      const t = Date.parse(lastVisitIso);
      if (!Number.isNaN(t)) return t;
    }
    // Calm default — last 24h window so the strip always has content
    return Date.now() - 24 * _MS_HOUR;
  }, [lastVisitIso]);

  const highlights = useMemo(
    () => deriveHighlights({ deal, documents, intel, sinceMs }),
    [deal, documents, intel, sinceMs],
  );

  // Calm fallback when nothing meaningful happened — keep the strip
  // visible but quiet, so the operator still feels oriented.
  const placeholder = highlights.length === 0;

  return (
    <section
      /* iter-183 — softer integrated strip per the Operational
       *  Rhythm Polish brief. Hairline divider above (the same
       *  border-top approach Linear / Stripe use for ambient rails),
       *  no heavy card, no shadow. Inline horizontal narration. */
      className="px-1 py-2.5 flex flex-wrap items-center gap-x-5 gap-y-1.5"
      style={{
        borderTop: "1px solid rgba(15,23,42,0.06)",
      }}
      data-testid={testid}
    >
      <div className="flex items-center gap-2 shrink-0">
        <Clock size={11} className="text-slate-400" strokeWidth={2} aria-hidden />
        <span className="text-[11px] font-semibold uppercase tracking-[0.10em] text-slate-500">Since your last visit</span>
        <span className="text-[11px] text-slate-400">· {fmtRelative(sinceMs)}</span>
      </div>

      {/* Highlights — calm horizontal rhythm */}
      <div className="flex items-center gap-x-4 gap-y-1 flex-wrap min-w-0 flex-1">
        {placeholder ? (
          <span className="text-[12px] text-slate-500">No new movement — calm and on track.</span>
        ) : (
          highlights.map((h, i) => {
            const tone = STAT_TONE[h.tone] || STAT_TONE.indigo;
            const Icon = h.Icon;
            return (
              <span
                key={i}
                className="inline-flex items-center gap-1.5 text-[12px] font-medium text-slate-700"
                data-testid={`${testid}-stat-${i}`}
              >
                <Icon size={11} style={{ color: tone.fg }} strokeWidth={2.4} />
                {h.label}
              </span>
            );
          })
        )}
      </div>

      {/* View-all affordance — inline, calm */}
      {!placeholder && (
        <button
          type="button"
          onClick={onJumpToTimeline}
          className="inline-flex items-center gap-0.5 text-[11.5px] font-semibold text-violet-700 hover:text-violet-900 shrink-0"
          data-testid={`${testid}-view-all`}
        >
          View all updates <ArrowUpRight size={11} strokeWidth={2.2} />
        </button>
      )}
    </section>
  );
};

export default SinceLastVisitStrip;
